import useApplicationList from 'api/versionControl/useApplicationList/useApplicationList';
import useDeleteApplication from 'api/versionControl/useDeleteApplication/useDeleteApplication';
import useEditApplication from 'api/versionControl/useEditApplication/useEditApplication';
import useInsertApplication from 'api/versionControl/useInsertApplication/useInsertApplication';
import ApplicationEditModal from 'components/private/Modals/versionControl/ApplicationEditModal/ApplicationEditModal';
import ApplicationInsertModal from 'components/private/Modals/versionControl/ApplicationInsertModal/ApplicationInsertModal';
import DeleteModal from 'components/shared/DeleteModal/DeleteModal';
import ApiPaginateTable from 'components/shared/Table/ApiPaginateTable';
import { permissions } from 'hooks/permissions/permissions';
import { usePermissions } from 'hooks/permissions/usePermissions';
import useApplicationsTableColumns from 'hooks/versionControl/useApplicationsTableColumns';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import PageSpinner from '../../components/shared/pageSpinner/PageSpinner';

const ApplicationsPage = () => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState('100');
  const [selectedRows, setSelectedRows] = useState([]);
  const [getApplicationList, loading, applicationList, pageRef] =
    useApplicationList();

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isInsertModalOpen, setIsInsertModalOpen] = useState(false);
  const [deleteChosenApplication, setDeleteChosenApplication] = useState({});
  const [editChosenApplication, setEditChosenApplication] = useState({});
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [insertLoading, setInsertLoading] = useState(false);
  const [applicationEditValues, setApplicationEditValues] = useState({});
  const [insertInputValue, setInsertInputValue] = useState('');

  const [deleteRequest] = useDeleteApplication();
  const [editRequest] = useEditApplication();
  const [insertRequest] = useInsertApplication();

  const navigate = useNavigate();
  const { can } = usePermissions();

  const versionControlPermissions =
    permissions.general.categories.keySettings.basicSettings.versionControl;

  const canCreate = can(versionControlPermissions.create);
  const canDelete = can(versionControlPermissions.delete);
  const canEdit = can(versionControlPermissions.edit);
  const canDetail = can(versionControlPermissions.versions.view);

  const tableRef = useRef(null);

  useEffect(() => {
    getApplicationList({ page: 1, count: pageSize });
  }, []);

  useEffect(() => {
    getApplicationList({ page, count: pageSize });
  }, [pageSize]);

  const goToInfoPage = useCallback(
    row => () => navigate(`/version-control/${row.id}`),
    [],
  );
  const deleteItem = useCallback(
    row => () => {
      setDeleteChosenApplication(row);
      setIsDeleteModalOpen(true);
    },
    [],
  );
  const editItem = useCallback(
    row => () => {
      setApplicationEditValues(row);
      setIsEditModalOpen(true);
    },
    [],
  );

  const [columnsData] = useApplicationsTableColumns(
    goToInfoPage,
    deleteItem,
    editItem,
    tableRef,
    {
      canDetail,
      canEdit,
      canDelete,
    },
  );

  const deleteHandle = id => {
    setDeleteLoading(true);
    deleteRequest(
      id,
      getApplicationList,
      setIsDeleteModalOpen,
      setDeleteLoading,
    );
  };

  const editHandle = data => {
    if (data) {
      setEditLoading(true);
      editRequest(
        {
          guid: applicationEditValues.id,
          name: data.title,
          key: data.key,
        },
        getApplicationList,
        setIsEditModalOpen,
        setEditLoading,
      );
    }
  };

  const insertHandle = payload => {
    setInsertLoading(true);
    insertRequest(
      () => getApplicationList({ page, count: pageSize }),
      setIsInsertModalOpen,
      setInsertLoading,
      payload,
    );
  };

  const closeDeleteModal = () => setIsDeleteModalOpen(false);
  const closeEditModal = () => setIsEditModalOpen(false);

  function getPageData(pageNumber) {
    setPage(pageNumber);
    getApplicationList({ page: pageNumber, count: pageSize });
  }

  function handleSearch(text) {
    setPage(1);
    getApplicationList({ page: 1, count: pageSize });
  }

  return (
    <>
      <div>{pageRef.refTitle}</div>
      {Object.keys(applicationList).length !== 0 ? (
        <ApiPaginateTable
          columns={columnsData}
          rowsData={applicationList[page]}
          rows={applicationList[page]}
          pageSize={pageSize}
          isDeletable={canDelete}
          isLoading={loading}
          addLable={canCreate ? 'ثبت برنامه‌ی جدید' : undefined}
          onAddClick={canCreate ? () => setIsInsertModalOpen(true) : undefined}
          setPageSize={val => setPageSize(val)}
          apiRef={tableRef}
          onPageChange={p => getPageData(p)}
          page={page}
          countPages={
            pageRef?.totalCount ? Math.ceil(pageRef.totalCount / pageSize) : 1
          }
          handleSearchApi={handleSearch}
          setSelectedRows={rows => setSelectedRows(rows)}
          selectedRows={selectedRows}
        />
      ) : (
        <PageSpinner loading />
      )}
      {canDelete ? (
        <DeleteModal
          open={isDeleteModalOpen}
          handleClose={closeDeleteModal}
          locationId={deleteChosenApplication.id}
          title={deleteChosenApplication.title}
          deleteLoading={deleteLoading}
          onDelete={deleteHandle}
        />
      ) : null}
      {canEdit ? (
        <ApplicationEditModal
          open={isEditModalOpen}
          handleClose={closeEditModal}
          editLoading={editLoading}
          onEdit={editHandle}
          initialValues={applicationEditValues}
        />
      ) : null}
      {canCreate ? (
        <ApplicationInsertModal
          open={isInsertModalOpen}
          handleClose={() => setIsInsertModalOpen(false)}
          insertLoading={insertLoading}
          onInsert={insertHandle}
          insertInputValue={insertInputValue}
          insertInputOnchange={e => setInsertInputValue(e.target.value)}
        />
      ) : null}
    </>
  );
};

export default ApplicationsPage;
